/**
 * Ad Benchmarks / SOV-Takeovers — the editable source of truth for the "Ad
 * Unit Benchmarks" section on client campaign report pages. One component,
 * two benchmark sets:
 *   - standard ("Ad Benchmarks"): the four ad-format rows.
 *   - sov ("SOV/Takeover Benchmarks"): the same formats for sponsorship/takeover line
 *     items, plus the blended "100% SOV Takeover" row. A format row with no
 *     numbers of its own is a placeholder that shows the Ad Benchmarks values.
 * Values are free-form display strings ("0.03%", "N/A") shown verbatim on the
 * web report and the XLSX. Saves apply to every page — including finalized
 * ones — on next view. "Copy for email" copies the saved rates as a
 * paste-ready table.
 *
 * A monthly GAM refresh (or "Recompute now" here) recomputes both sets from
 * the last 3 full months of delivery and replaces the numbers on rows with
 * enough data — hand edits included; labels and notes are kept. The banner
 * shows the last refresh and polls while one is running; each row says where
 * its numbers came from and how many campaigns stand behind them. Below it,
 * Ad Ops manages the test/house orders the refresh leaves out.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Spinner, StatusPill, tableStyles } from '../ui';
import ConfirmDialog from '../ConfirmDialog';
import {
  AdBenchmarksRefreshStatus,
  AdUnitBenchmarkKey,
  BenchmarkSetId,
  getAdBenchmarks,
  getAdBenchmarksRefreshStatus,
  getSovBenchmarks,
  saveAdBenchmarks,
  saveSovBenchmarks,
  startAdBenchmarksRefresh,
} from '../../services/adBenchmarksApi';
import {
  BenchmarkDraft,
  BenchmarkField,
  BenchmarkTabModel,
  canBePlaceholder,
  draftFromModel,
  editField,
  isDirty,
  modelFromSov,
  modelFromStandard,
  resetRow,
  sovSavePayload,
  standardSavePayload,
} from './AdBenchmarks/benchmarkDraft';
import { buildBenchmarkEmail } from './AdBenchmarks/benchmarkEmail';
import ExcludedOrdersPanel from './AdBenchmarks/ExcludedOrdersPanel';
import {
  BannerTone,
  ROW_SOURCE_PILL,
  emailContext,
  formatWindow,
  refreshBanner,
  rowSource,
  sampleSummary,
} from './AdBenchmarks/benchmarkRefresh';
import { copyRichText } from '../../utils/clipboard';

interface AdBenchmarksTabProps {
  set: BenchmarkSetId;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const COPIED_MS = 2000;
/** How often the tab re-reads the refresh status while a run is going. */
const REFRESH_POLL_MS = 5000;

const FIELD_COLUMNS: { field: BenchmarkField; heading: string; width: number }[] = [
  { field: 'ctr', heading: 'CTR Benchmark', width: 110 },
  { field: 'ctrRange', heading: 'CTR Range', width: 130 },
  { field: 'vcr', heading: 'Completion Rate Benchmark', width: 130 },
  { field: 'vcrRange', heading: 'Completion Rate Range', width: 140 },
  { field: 'note', heading: 'Client-Facing Note', width: 260 },
];

const REFRESH_INTRO =
  'Each month the numbers are recomputed from GAM — the typical (median) campaign and the middle 50% of ' +
  'campaigns over the last 3 full months — replacing hand edits on rows with enough data; labels and notes are kept.';

const COPY: Record<BenchmarkSetId, { title: string; emailTitle: string; intro: string }> = {
  standard: {
    title: 'Ad Unit Benchmarks',
    emailTitle: 'Culture Genesis — Ad Unit Benchmarks',
    intro:
      'Benchmarks for standard (non-sponsorship) line items, shown on client campaign report pages (web and ' +
      'XLSX) — changes go live on next page view. Reports show only the rows matching the campaign’s ad formats; ' +
      'when none can be detected, all rows are shown. Values are shown exactly as written (e.g. “0.03%”, “N/A”). ' +
      `Takeover benchmarks are on the SOV/Takeover Benchmarks tab. ${REFRESH_INTRO}`,
  },
  sov: {
    title: 'SOV/Takeover Benchmarks',
    emailTitle: 'Culture Genesis — SOV/Takeover Benchmarks',
    intro:
      'Benchmarks for sponsorship (SOV/takeover) line items. A takeover campaign’s report shows the row for each ' +
      'of its formats once that row has its own numbers. Rows marked Placeholder have none yet: they show the Ad ' +
      'Benchmarks values here, and client reports show the Ad Benchmarks row plus “100% SOV Takeover” instead. ' +
      `“100% SOV Takeover” covers all takeover formats (also used in media plans). ${REFRESH_INTRO}`,
  },
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  boxSizing: 'border-box',
  background: 'var(--bg-surface-1)',
  color: 'var(--fg-1)',
};

const placeholderInputStyle: React.CSSProperties = { ...inputStyle, color: 'var(--fg-3)', fontStyle: 'italic' };

const bannerBase: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  color: 'var(--fg-1)',
  fontSize: 13,
};

const BANNER_BACKGROUND: Record<BannerTone, string> = {
  neutral: 'var(--bg-surface-3)',
  running: 'var(--info-soft)',
  failed: 'var(--danger-soft)',
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const AdBenchmarksTab: React.FC<AdBenchmarksTabProps> = ({ set, notify }) => {
  const [model, setModel] = useState<BenchmarkTabModel | null>(null);
  const [draft, setDraft] = useState<BenchmarkDraft | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState<AdBenchmarksRefreshStatus | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [refreshedWhileEditing, setRefreshedWhileEditing] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    setConflict(false);
    setRefreshedWhileEditing(false);
    try {
      const next = set === 'sov' ? modelFromSov(await getSovBenchmarks()) : modelFromStandard(await getAdBenchmarks());
      setModel(next);
      setDraft(draftFromModel(next));
    } catch {
      setLoadError(true);
    }
  }, [set]);

  /** The banner is optional: without a status the table still loads and saves. */
  const loadStatus = useCallback(async () => {
    try {
      setStatus(await getAdBenchmarksRefreshStatus());
    } catch {
      // keep the last status (or none) — polling retries
    }
  }, []);

  useEffect(() => {
    load();
    loadStatus();
  }, [load, loadStatus]);

  // A flag + effect (not an inline timeout) so an unmount mid-flash can't set state.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const dirty = useMemo(() => Boolean(model && draft && isDirty(draft, model)), [model, draft]);

  // Poll while a run is going (it takes minutes; the server answers 'failed'
  // if the run's process died, so this always ends).
  const running = status?.state === 'running';
  useEffect(() => {
    if (!running) return undefined;
    const interval = window.setInterval(loadStatus, REFRESH_POLL_MS);
    return () => window.clearInterval(interval);
  }, [running, loadStatus]);

  // A run finishing: pick up the new values (unless that would discard edits) and say what happened.
  const previousState = useRef<AdBenchmarksRefreshStatus['state'] | null>(null);
  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);
  useEffect(() => {
    const was = previousState.current;
    previousState.current = status?.state ?? null;
    if (was !== 'running' || !status || status.state === 'running') return;
    if (status.state === 'failed') {
      notify('error', 'GAM refresh failed', 'No benchmarks were changed.');
      return;
    }
    const changed = status.lastRun?.changes?.length ?? 0;
    notify(
      'success',
      'Benchmarks refreshed from GAM',
      changed > 0 ? `${changed} value${changed === 1 ? '' : 's'} changed across both tabs.` : 'No values changed.',
    );
    if (dirtyRef.current) setRefreshedWhileEditing(true);
    else load();
  }, [status, notify, load]);

  const setField = (key: AdUnitBenchmarkKey, field: BenchmarkField, value: string) => {
    setDraft((prev) => (prev ? editField(prev, key, field, value) : prev));
  };

  const reset = (key: AdUnitBenchmarkKey) => {
    setDraft((prev) => (prev && model ? resetRow(prev, model, key) : prev));
  };

  const save = async () => {
    if (!draft || !model) return;
    setSaving(true);
    try {
      const next =
        set === 'sov'
          ? modelFromSov(await saveSovBenchmarks(sovSavePayload(draft, model), model.updatedAt))
          : modelFromStandard(await saveAdBenchmarks(standardSavePayload(draft, model), model.updatedAt));
      setModel(next);
      setDraft(draftFromModel(next));
      notify('success', 'Benchmarks saved', 'Live on all campaign report pages on next view.');
    } catch (err: any) {
      if (err?.status === 409) setConflict(true);
      else notify('error', 'Failed to save benchmarks', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setConfirmOpen(false);
    setStarting(true);
    try {
      await startAdBenchmarksRefresh();
    } catch (err: any) {
      if (err?.status === 409) {
        notify('error', 'A GAM refresh is already running', 'This tab updates when it finishes.');
      } else {
        notify('error', 'Couldn’t start the GAM refresh', err?.message);
        setStarting(false);
        return;
      }
    }
    await loadStatus();
    setStarting(false);
  };

  const copyForEmail = async () => {
    if (!model) return;
    const keys = model.order.filter((key) => model.rows[key]);
    const rows = keys.map((key) => model.rows[key]!);
    const sources = keys.map((key) => rowSource(model.computed, key, model.rows[key], model.placeholders.includes(key)));
    const context = emailContext(model.computed, sources);
    const { html, text } = buildBenchmarkEmail({ title: COPY[set].emailTitle, rows, ...context });
    try {
      await copyRichText(html, text);
      setCopied(true);
      const placeholders = model.placeholders.length;
      const hints = [
        placeholders > 0 ? `${placeholders} of these rows are placeholders (same as Ad Benchmarks).` : '',
        model.computed && !context.subtitle
          ? 'The “Based on campaigns delivered…” line was left out — not every row comes from the latest GAM refresh.'
          : '',
      ].filter(Boolean);
      notify('success', 'Copied — paste into your email', hints.length > 0 ? hints.join(' ') : undefined);
    } catch {
      notify('error', 'Copy failed', 'Your browser blocked clipboard access.');
    }
  };

  if (loadError) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13.5 }}>
        Couldn’t load the benchmarks.{' '}
        <Button variant="subtle" size="sm" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }

  if (!model || !draft) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner />
      </div>
    );
  }

  const banner = status ? refreshBanner(status, model.computed) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--fg-1)' }}>{COPY[set].title}</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--fg-3)', maxWidth: 640 }}>{COPY[set].intro}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {model.updatedAt && (
            <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
              Last updated {fmtDateTime(model.updatedAt)}
              {model.updatedBy ? ` by ${model.updatedBy}` : ''}
            </span>
          )}
          <Button
            variant="ghost"
            onClick={copyForEmail}
            disabled={dirty}
            title={dirty ? 'Save or reset your changes first — this copies the saved values' : 'Copy these rates as a table to paste into an email'}
          >
            {copied ? 'Copied ✓' : 'Copy for email'}
          </Button>
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {status && banner && (
        <div
          role={banner.tone === 'failed' ? 'alert' : 'status'}
          aria-label="GAM refresh"
          style={{ ...bannerBase, background: BANNER_BACKGROUND[banner.tone] }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {banner.tone === 'running' && <Spinner size={14} />}
            {banner.text}
          </span>
          {status.state !== 'running' && (
            <Button variant="subtle" size="sm" onClick={() => setConfirmOpen(true)} disabled={starting}>
              {starting ? 'Starting…' : status.state === 'failed' ? 'Retry' : 'Recompute now'}
            </Button>
          )}
        </div>
      )}

      {status && (
        <ExcludedOrdersPanel
          orders={status.excludedOrders ?? []}
          onSaved={(excludedOrders) => setStatus((prev) => (prev ? { ...prev, excludedOrders } : prev))}
          notify={notify}
        />
      )}

      {refreshedWhileEditing && (
        <div role="alert" style={{ ...bannerBase, background: 'var(--warning-soft)' }}>
          <span>
            New numbers were published from GAM while you were editing. Reload to see them — your unsaved edits
            will be discarded.
          </span>
          <Button variant="subtle" size="sm" onClick={load}>
            Reload
          </Button>
        </div>
      )}

      {conflict && (
        <div role="alert" style={{ ...bannerBase, background: 'var(--warning-soft)' }}>
          <span>
            These benchmarks changed since you opened this tab, so your edits weren’t saved. Reload to see the
            latest values, then re-apply your edits.
          </span>
          <Button variant="subtle" size="sm" onClick={load}>
            Reload
          </Button>
        </div>
      )}

      <div style={tableStyles.wrapScroll}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              <th style={{ ...tableStyles.th, minWidth: 150 }}>Ad Unit</th>
              {FIELD_COLUMNS.map((c) => (
                <th key={c.field} style={{ ...tableStyles.th, minWidth: c.width }}>
                  {c.heading}
                </th>
              ))}
              {model.computed && <th style={{ ...tableStyles.th, minWidth: 150 }}>Based on</th>}
              <th style={tableStyles.th} />
            </tr>
          </thead>
          <tbody>
            {model.order.map((key) => {
              const row = draft.rows[key];
              if (!row) return null;
              const placeholder = draft.placeholders.includes(key);
              const source = rowSource(model.computed, key, row, placeholder);
              const sourcePill = source === 'gam' || source === 'edited' || source === 'not-enough-data' ? ROW_SOURCE_PILL[source] : null;
              const computedRow = model.computed?.rows[key];
              const sample = computedRow ? sampleSummary(computedRow) : null;
              return (
                <tr key={key}>
                  <td style={{ ...tableStyles.td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                      {row.label}
                      {placeholder && (
                        <span title="No numbers of its own yet — showing the Ad Benchmarks values. Edit any field to give this row its own.">
                          <StatusPill variant="draft" noDot uppercase={false}>
                            Placeholder · same as Ad Benchmarks
                          </StatusPill>
                        </span>
                      )}
                      {sourcePill && (
                        <span title={sourcePill.title}>
                          <StatusPill variant={sourcePill.variant} noDot uppercase={false}>
                            {sourcePill.label}
                          </StatusPill>
                        </span>
                      )}
                    </div>
                  </td>
                  {FIELD_COLUMNS.map((c) => (
                    <td key={c.field} style={tableStyles.td}>
                      <input
                        style={placeholder ? placeholderInputStyle : inputStyle}
                        value={row[c.field] ?? ''}
                        placeholder={c.field === 'note' ? '—' : undefined}
                        onChange={(e) => setField(key, c.field, e.target.value)}
                        aria-label={`${row.label} ${c.heading}`}
                      />
                    </td>
                  ))}
                  {model.computed && (
                    <td style={{ ...tableStyles.td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--fg-3)' }}>
                      {sample ? <span title={sample.title}>{sample.label}</span> : '—'}
                    </td>
                  )}
                  <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                    {canBePlaceholder(model, key) ? (
                      <Button variant="ghost" size="sm" onClick={() => reset(key)} disabled={placeholder}>
                        Reset to placeholder
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => reset(key)}>
                        Reset to default
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ margin: 0, fontSize: 12, color: 'var(--fg-4)' }}>
        The ad-unit rows are fixed — they map 1:1 to how campaign line items are classified. Adding a new ad
        unit type needs an engineering change.
      </p>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Recompute from GAM?"
        message={
          `This recomputes both benchmark tabs from GAM campaigns delivered ${status ? formatWindow(status.window) : 'in the last 3 full months'}. ` +
          'Rows with enough data get new numbers — hand edits included — and client campaign report pages show them on ' +
          'their next view. Labels and notes stay as they are. It takes a few minutes.'
        }
        confirmText="Recompute"
        onConfirm={recompute}
        onCancel={() => setConfirmOpen(false)}
        variant="warning"
      />
    </div>
  );
};

export default AdBenchmarksTab;
